var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Group, Mesh, MeshPhongMaterial } from "three";
import { meshLoaderService } from "../rendering/mesh/MeshLoaderService";
import { disposeMesh } from "./dispose";
import { AvatarStyle, MulticastDelegate1 } from "flavr-common";
import { addBarycentricCoordinates, createWireframeMaterial } from "../rendering/shader/BarycentricWireframe";
import { applyColorsToMesh } from "./utils";
export var AccessoireGroup;
(function (AccessoireGroup) {
    AccessoireGroup["Hat"] = "hat";
    AccessoireGroup["Shoulder"] = "shoulder";
})(AccessoireGroup || (AccessoireGroup = {}));
const accessoiresDefMap = new Map();
function registerAccessoire(accessoire) {
    if (accessoiresDefMap.has(accessoire.id)) {
        console.error(`registerAccessoire: already registerd`, accessoire.id, accessoiresDefMap.get(accessoire.id));
        return;
    }
    accessoiresDefMap.set(accessoire.id, accessoire);
}
const accessoires = [
    {
        id: 0,
        url: "/meshes/character-accessoires/Fly.obj",
        group: AccessoireGroup.Shoulder
    },
    {
        id: 1,
        url: "/meshes/character-accessoires/Hosentraeger.obj",
        group: AccessoireGroup.Shoulder
    },
    {
        id: 2,
        url: "/meshes/character-accessoires/Suit.obj",
        group: AccessoireGroup.Shoulder
    },
    {
        id: 3,
        url: "/meshes/character-accessoires/Tie.obj",
        group: AccessoireGroup.Shoulder
    },
    {
        id: 4,
        url: "/meshes/character-accessoires/Helmet.obj",
        group: AccessoireGroup.Hat,
        hideHair: true,
        targetPoint: {
            monitor: {
                pos: [0, 0.08, 0],
                ori: [0, 0, 0, 1],
                scale: [1, 1, 1]
            }
        }
    },
    {
        id: 5,
        url: "/meshes/character-accessoires/CarpenterHat.obj",
        group: AccessoireGroup.Hat,
        targetPoint: {
            monitor: {
                pos: [0, 0.04, 0],
                ori: [0, 0, 0, 1],
                scale: [1, 1, 1]
            }
        }
    },
    {
        id: 6,
        url: "/meshes/character-accessoires/EarProtector.obj",
        group: AccessoireGroup.Hat,
        targetPoint: {
            monitor: {
                pos: [0, 0.0, 0],
                ori: [0, 0, 0, 1],
                scale: [1, 1, 1]
            }
        }
    },
    {
        id: 7,
        url: "/meshes/character-accessoires/AluminiumHat.obj",
        group: AccessoireGroup.Hat,
        hideHair: true,
        targetPoint: {
            monitor: {
                pos: [0, 0.1, 0],
                ori: [0, 0, 0, 1],
                scale: [1, 1, 1]
            }
        }
    },
    {
        id: 8,
        url: "/meshes/character-accessoires/BaseballCap.obj",
        group: AccessoireGroup.Hat,
        hideHair: true,
        targetPoint: {
            monitor: {
                pos: [0, 0.09, 0],
                ori: [0.0174524, 0, 0, 0.9998477], // quaternion for 2° rotation on X-Axis.
                scale: [1, 1, 1]
            }
        }
    },
    {
        id: 12,
        url: "/meshes/character-accessoires/BowlerHat.obj",
        group: AccessoireGroup.Hat,
        hideHair: true,
        targetPoint: {
            monitor: {
                pos: [0, 0.08, 0],
                ori: [0.1305262, 0, 0, 0.9914449], // quaternion for 15° rotation on X-Axis.
                scale: [1, 1, 1]
            }
        }
    },
    {
        id: 13,
        url: "/meshes/character-accessoires/CylinderHat.obj",
        group: AccessoireGroup.Hat,
        hideHair: true,
        targetPoint: {
            monitor: {
                pos: [0, 0.06, 0],
                ori: [0.0174524, 0, 0, 0.9998477], // quaternion for 2° rotation on X-Axis.
                scale: [1, 1, 1]
            }
        }
    },
    {
        id: 14,
        url: "/meshes/character-accessoires/AsianHat.obj",
        group: AccessoireGroup.Hat,
        targetPoint: {
            monitor: {
                pos: [0, 0.05, 0],
                ori: [0.0174524, 0, 0, 0.9998477], // quaternion for 2° rotation on X-Axis.
                scale: [1, 1, 1]
            }
        }
    },
    {
        id: 18,
        url: "/meshes/character-accessoires/Scarf.obj",
        group: AccessoireGroup.Shoulder,
        targetPoint: {
            monitor: {
                pos: [0, 0.0, 0],
                ori: [0, 0, 0, 1],
                scale: [1, 1, 1]
            }
        }
    }
];
// automatically executed when file is imported.
for (const accessoire of accessoires) {
    registerAccessoire(accessoire);
}
export class AccessoireNode {
    constructor(def) {
        this.def = def;
        this.customColor = 0xffffff;
        this.isDestroyed = false;
        this.group = new Group();
        this.onLoadedDelegate = new MulticastDelegate1();
        this.onDestroyedDelegate = new MulticastDelegate1();
        this.group.matrixAutoUpdate = false;
    }
    isLoaded() {
        return this.group.children.length > 0;
    }
    isVisible() {
        if (this.isDestroyed)
            return false;
        if (!this.isLoaded())
            return false;
        return this.group.visible;
    }
    setColor(baseColor) {
        this.customColor = baseColor;
        applyColorsToMesh(this.group, this.customColor);
    }
    load() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const group = yield meshLoaderService.load(this.def.url);
                if (this.isDestroyed)
                    return;
                const clonedGroup = group.clone(true);
                clonedGroup.traverse((node) => {
                    node.matrixAutoUpdate = false;
                    if (!(node instanceof Mesh))
                        return;
                    // the following might be better placed in some kind of AccessoireMeshProvider:
                    if (node.name.endsWith("_colorFrame")) {
                        node.material = new MeshPhongMaterial();
                    }
                    else {
                        addBarycentricCoordinates(node.geometry);
                        node.material = createWireframeMaterial();
                    }
                });
                this.group.add(clonedGroup);
                applyColorsToMesh(this.group, this.customColor);
                this.onLoadedDelegate.invoke(this);
            }
            catch (error) {
                console.error("Accessoire::load error", error);
            }
        });
    }
    applyToTargetPoint(style) {
        if (this.isDestroyed)
            return;
        this.group.position.set(0, 0, 0);
        this.group.quaternion.identity();
        this.group.scale.set(1, 1, 1);
        this.group.updateMatrix();
        if (!this.def.targetPoint)
            return;
        if (typeof this.def.targetPoint === "string") {
            const obj = this.group.getObjectByName(this.def.targetPoint);
            if (!obj) {
                throw new Error(`applyToTargetPoint: can't find targetPoint ${this.def.targetPoint} in group ${this.group.name}`);
            }
            this.group.position.copy(obj.position);
            this.group.quaternion.copy(obj.quaternion);
            this.group.scale.copy(obj.scale);
            this.group.updateMatrix();
            return;
        }
        if (typeof this.def.targetPoint !== "object") {
            throw new TypeError(`applyToTargetPoint: accessoire with id ${this.def.id} should have targetPoint with typeof object`);
        }
        let styleStr = AvatarStyle[style];
        if (!styleStr) {
            throw new Error(`applyToTargetPoint style ${JSON.stringify(style)} isn't defined in AvatarStyle`);
        }
        styleStr = styleStr.toLowerCase();
        const target = this.def.targetPoint[styleStr];
        if (!target) {
            return;
        }
        if (typeof target === "string") {
            const obj = this.group.getObjectByName(target);
            if (!obj) {
                throw new Error(`applyToTargetPoint: can't find targetPoint ${this.def.targetPoint} in group ${this.group.name}`);
            }
            this.group.position.copy(obj.position);
            this.group.quaternion.copy(obj.quaternion);
            this.group.scale.copy(obj.scale);
            this.group.updateMatrix();
            return;
        }
        this.group.position.fromArray(target.pos);
        this.group.quaternion.fromArray(target.ori);
        this.group.scale.fromArray(target.scale);
        this.group.updateMatrix();
    }
    destroy() {
        this.isDestroyed = true;
        this.onLoadedDelegate.clear();
        disposeMesh(this.group);
        if (this.group.parent) {
            this.group.removeFromParent();
        }
        this.onDestroyedDelegate.invoke(this);
        this.onDestroyedDelegate.clear();
    }
}
export class AvatarAccessoires {
    constructor() {
        this.accessoires = new Map();
    }
    static getAccessoireDef(id) {
        return accessoiresDefMap.get(id);
    }
    static isValidId(id) {
        return accessoiresDefMap.has(id);
    }
    add(id) {
        const def = AvatarAccessoires.getAccessoireDef(id);
        if (!def)
            return undefined;
        if (this.accessoires.has(id))
            return undefined;
        const accessoire = new AccessoireNode(def);
        this.accessoires.set(id, accessoire);
        return accessoire;
    }
    remove(id) {
        const accessoire = this.accessoires.get(id);
        if (!accessoire)
            return;
        this.accessoires.delete(id);
        accessoire.destroy();
    }
    removeByGroup(group) {
        for (const accessoire of this.accessoires.values()) {
            if (group !== accessoire.def.group)
                continue;
            this.remove(accessoire.def.id);
        }
    }
    removeAll() {
        for (const accessoire of this.accessoires.values()) {
            accessoire.destroy();
        }
        this.accessoires.clear();
    }
    shouldHideHair() {
        for (const accessoire of this.accessoires.values()) {
            if (accessoire.def.hideHair) {
                return true;
            }
        }
        return false;
    }
    onStyleChanged(style) {
        for (const accessoire of this.accessoires.values()) {
            accessoire.applyToTargetPoint(style);
        }
    }
    static *iconsByGroupIterator(group) {
        for (const def of accessoiresDefMap.values()) {
            if (group !== def.group)
                continue;
            yield [def.id, ""];
        }
    }
}
